import React, { useState, useEffect } from 'react';
import { userService } from '../services/userService';
import { LogOut, Users, BarChart3, Settings, Trash2, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { authService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { ROLE_NAMES } from '../utils/permissions';

export default function MasterAdminDashboard({ currentUser }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [analytics, setAnalytics] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [message, setMessage] = useState('');

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [analyticsData, usersData, auditData] = await Promise.all([
        userService.getSystemAnalytics(),
        userService.getAllUsers(),
        userService.getSystemAuditLog(50)
      ]);

      setAnalytics(analyticsData);
      setAllUsers(usersData);
      setFilteredUsers(usersData);
      setAuditLog(auditData);
    } catch (error) {
      console.error('Dashboard error:', error);
      
      // Check if it's a permission error
      if (error.message.includes('permission') || error.code === 'permission-denied') {
        setMessage(
          '⚠️ Permission Denied: You need to update Firestore Security Rules. ' +
          'Run: firebase deploy --only firestore:rules in your terminal. ' +
          'See firestore.rules file for the required rules.'
        );
      } else {
        setMessage('Error loading dashboard data: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = allUsers;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterRole) {
      filtered = filtered.filter(user => user.currentRole === filterRole);
    }

    if (filterStatus) {
      filtered = filtered.filter(user => user.status === filterStatus);
    }

    setFilteredUsers(filtered);
  }, [searchTerm, filterRole, filterStatus, allUsers]);

  const handleDeleteUser = async () => {
    if (!deletingUserId) return;

    try {
      const result = await userService.deleteUser(deletingUserId);
      if (result.success) {
        setMessage('User deleted successfully');
        setShowDeleteConfirm(false);
        setDeletingUserId(null);
        setSelectedUser(null);
        loadDashboardData();
      } else {
        setMessage('Error: ' + result.message);
      }
    } catch (error) {
      setMessage('Error deleting user: ' + error.message);
    }
  };

  const handleSuspendUser = async (userId, suspend = true) => {
    try {
      const result = await userService.suspendUser(userId, suspend);
      if (result.success) {
        setMessage(result.message);
        loadDashboardData();
      } else {
        setMessage('Error: ' + result.message);
      }
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const handleSignOut = async () => {
    await authService.signout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-xl">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition mr-4"
            >
              <span>←</span>
              Back
            </button>
            <img 
              src="/images/fhni%20logo.png" 
              alt="Fire Hive Network" 
              className="h-8 w-8 object-contain cursor-pointer hover:opacity-80 transition" 
              onClick={() => navigate('/')} 
            />
            <span 
              className="text-2xl font-black cursor-pointer hover:opacity-80 transition" 
              onClick={() => navigate('/')}
            >
              FIRE HIVE
            </span>
            <span className="ml-4 px-3 py-1 bg-red-600/20 text-red-400 text-xs font-bold rounded-full">
              MASTER ADMIN
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm text-gray-400">Godman {currentUser.displayName || 'User'}</span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 px-4 py-2 rounded-lg transition"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      {/* Message Alert */}
      {message && (
        <div className="bg-blue-600/20 border border-blue-600 text-blue-400 px-6 py-3 m-6 rounded-lg flex items-center gap-2 max-w-7xl mx-auto">
          <AlertCircle size={20} />
          <div className="flex-1">
            {message}
            {message.includes('firebase deploy') && (
              <div className="text-xs text-blue-300 mt-2">
                💡 Tip: Copy and paste this command: <code className="bg-black/30 px-2 py-1 rounded">firebase deploy --only firestore:rules</code>
              </div>
            )}
          </div>
          <button onClick={() => setMessage('')} className="ml-auto text-sm hover:underline flex-shrink-0">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6 border-b border-zinc-800 mb-8">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 pb-4 px-2 font-semibold border-b-2 transition ${
              activeTab === 'analytics'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <BarChart3 size={20} />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 pb-4 px-2 font-semibold border-b-2 transition ${
              activeTab === 'users'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <Users size={20} />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`flex items-center gap-2 pb-4 px-2 font-semibold border-b-2 transition ${
              activeTab === 'audit'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <Settings size={20} />
            Audit Log
          </button>
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <p className="text-gray-400 text-sm mb-2">Total Users</p>
                <p className="text-4xl font-black text-orange-500">{analytics.totalUsers}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <p className="text-gray-400 text-sm mb-2">Active Users</p>
                <p className="text-4xl font-black text-green-500">{analytics.activeUsers}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <p className="text-gray-400 text-sm mb-2">Inactive Users</p>
                <p className="text-4xl font-black text-red-500">{analytics.inactiveUsers}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <p className="text-gray-400 text-sm mb-2">Recent Signups (7d)</p>
                <p className="text-4xl font-black text-blue-500">{analytics.recentSignups?.length || 0}</p>
              </div>
            </div>

            {/* User Distribution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Role */}
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-white mb-4">Users by Role</h3>
                <div className="space-y-3">
                  {Object.entries(analytics.usersByRole).map(([role, count]) => (
                    <div key={role} className="flex justify-between items-center pb-3 border-b border-zinc-800">
                      <span className="text-gray-400 capitalize">{ROLE_NAMES[role] || role}</span>
                      <span className="bg-orange-600/20 text-orange-400 px-3 py-1 rounded-full font-bold">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Mountain */}
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-white mb-4">Users by Mountain</h3>
                <div className="space-y-3">
                  {Object.entries(analytics.usersByMountain).map(([mountain, count]) => (
                    <div key={mountain} className="flex justify-between items-center pb-3 border-b border-zinc-800">
                      <span className="text-gray-400 capitalize">{mountain}</span>
                      <span className="bg-blue-600/20 text-blue-400 px-3 py-1 rounded-full font-bold">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Signups */}
            {analytics.recentSignups?.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-white mb-4">Recent Signups</h3>
                <div className="space-y-2">
                  {analytics.recentSignups.map((user, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-zinc-800/50 rounded">
                      <div>
                        <p className="font-semibold">{user.fullName}</p>
                        <p className="text-sm text-gray-400">{user.email}</p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(user.createdAt?.toDate?.() || user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-orange-500 outline-none"
              />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:border-orange-500 outline-none"
              >
                <option value="">All Roles</option>
                {Object.entries(ROLE_NAMES).map(([key, name]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:border-orange-500 outline-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-800 border-b border-zinc-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Name</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Email</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Role</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-zinc-800 hover:bg-zinc-800/50 transition">
                        <td className="px-6 py-4 font-semibold">{user.fullName}</td>
                        <td className="px-6 py-4 text-sm text-gray-400">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className="text-xs bg-orange-600/20 text-orange-400 px-2 py-1 rounded capitalize">
                            {ROLE_NAMES[user.currentRole] || user.currentRole}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`text-xs px-2 py-1 rounded capitalize ${
                              user.status === 'active'
                                ? 'bg-green-600/20 text-green-400'
                                : 'bg-red-600/20 text-red-400'
                            }`}
                          >
                            {user.status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="text-sm bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 px-3 py-1 rounded transition"
                            >
                              View
                            </button>
                            {user.status === 'active' ? (
                              <button
                                onClick={() => handleSuspendUser(user.id, true)}
                                className="text-sm bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 px-3 py-1 rounded transition"
                              >
                                Suspend
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSuspendUser(user.id, false)}
                                className="text-sm bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-1 rounded transition"
                              >
                                Unsuspend
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setDeletingUserId(user.id);
                                setShowDeleteConfirm(true);
                              }}
                              className="text-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 px-3 py-1 rounded transition"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-sm text-gray-400">Showing {filteredUsers.length} of {allUsers.length} users</p>
          </div>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-800 border-b border-zinc-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">User</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Action</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Details</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map((log) => (
                      <tr key={log.id} className="border-b border-zinc-800 hover:bg-zinc-800/50 transition">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-sm">{log.userName}</p>
                            <p className="text-xs text-gray-500">{log.userEmail}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs bg-purple-600/20 text-purple-400 px-2 py-1 rounded capitalize">
                            {log.action?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {log.action === 'role_update' && (
                            <span>
                              {ROLE_NAMES[log.previousRole]} → {ROLE_NAMES[log.newRole]}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-400">
                          {new Date(log.timestamp?.toDate?.() || log.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedUser(null)}
        >
          <div
            className="bg-zinc-900 rounded-xl border border-zinc-800 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-orange-600 p-6 border-b border-zinc-800">
              <h2 className="text-2xl font-bold text-white">{selectedUser.fullName}</h2>
              <p className="text-orange-100 text-sm">{selectedUser.email}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Current Role</p>
                <p className="text-lg font-semibold capitalize">
                  {ROLE_NAMES[selectedUser.currentRole] || selectedUser.currentRole}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Mountain</p>
                <p className="text-lg font-semibold capitalize">
                  {selectedUser.mountainOfInfluence || 'Not assigned'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Status</p>
                <p
                  className={`text-lg font-semibold capitalize ${
                    selectedUser.status === 'active' ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {selectedUser.status || 'active'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Joined</p>
                <p className="text-sm">
                  {new Date(selectedUser.createdAt?.toDate?.() || selectedUser.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-3 pt-4 border-t border-zinc-800">
                <button
                  onClick={() => setSelectedUser(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-zinc-900 rounded-xl border border-red-600/50 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-red-600/20 p-6 border-b border-zinc-800">
              <h2 className="text-2xl font-bold text-red-400">Confirm Deletion</h2>
              <p className="text-red-300 text-sm">This action cannot be undone</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-300">
                Are you sure you want to delete this user? All associated data will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletingUserId(null);
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
